import type { FloorPath } from "building-types/floors/floorTypes";
import type { SolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import type {
  PierEntity,
  StemWallEntity,
  StripFootingEntity,
  TurndownFootingEntity,
  CircularIsolatedFootingEntity,
  RectangularIsolatedFootingEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import {
  isCircularIsolatedFooting,
  isCircularCMUPier,
  isCircularCIPPier,
  isRectangularCMUPier,
  isRectangularCIPPier,
} from "building-types/foundations/foundationComponents";
import { BrickLedgeType, SlabType, StemWallType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPath,
  FoundationSectionSize,
  IsolatedFoundationSectionSize,
} from "building-types/foundations/foundationTypes";
import { FoundationSectionType } from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import { worldInchesNew } from "common/utils/nominalNumbers";
import {
  isPorchFoundationCondition,
  isSpanFoundationCondition,
  isInteriorFoundationCondition,
  isPerimeterFoundationCondition,
} from "doc/utils/foundationSystemTools";
import { DIST_EPSILON_SQUARED } from "geom/core";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { vec2DistSquared } from "geom/vec2";
import { defaultFoundationDatumAbstractions } from "../datums/defaultDatumAbstractions";
import { floorPathTop } from "../floor/floorPathUtils";
import { brickLedgeTypeAtCondition, makeBrickLedgeSize } from "./brickLedge";
import type { FoundationPathEnds } from "./foundationStrip";
import {
  FoundationSectionAlignment,
  foundationPathFloorPerimeterEnds,
  foundationPathOffsetAlignment,
  foundationSectionId,
  isolatedFoundationPathOpenings,
  isolatedFoundationSectionSizeFromAlignment,
  linearFoundationSectionSizeFromAlignment,
  resolveFoundationComponentTopBottom,
  voidFoundationSectionSizeNew,
} from "./foundationStrip";
import { foundationPathCoordinates } from "./pathCoordinates";
import { slabDepthAtFoundationPath, slabTypeAtFoundationPath } from "./slabUtils";
import { stemWallWidth } from "./stemWall";

export function makeTurndownFootingSectionSizes({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  footing,
  slabSettings,
  doorSettings,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: TurndownFootingEntity;
  slabSettings: SlabSettings | undefined;
  doorSettings: DoorSettings;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const footingSize = makeTurndownFootingSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    footing,
    slabSettings,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  const brickLedgeSize = makeTurndownBrickLedgeSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    footing,
    doorSettings,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  return [footingSize, brickLedgeSize];
}

function makeTurndownFootingSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  footing,
  slabSettings,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: TurndownFootingEntity;
  slabSettings: SlabSettings | undefined;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { condition } = foundationPath;

  const sectionType = FoundationSectionType.COMPONENT;
  let sectionCode = RoomClass[RoomClass.HOUSE];
  if (isPorchFoundationCondition[condition]) {
    sectionCode = RoomClass[RoomClass.PORCH];
  }

  const sectionId = foundationSectionId(footing.componentType, sectionType, sectionCode);

  if (slabSettings && slabTypeAtFoundationPath({ condition }, slabSettings) === SlabType.NONE) {
    return voidFoundationSectionSizeNew(footing, sectionId);
  }

  const { levelDatumsGenerated } = level;
  const { foundation } = levelDatumsGenerated;

  const { footingWidth, material } = footing;
  const width = footingWidth;

  const ends = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });
  const { startCondition, endCondition } = ends;
  let { start, end } = ends;

  // special case for span condition footings - they extend past the floor perimeter
  if (isSpanFoundationCondition[startCondition]) {
    start = -width / 2;
  }
  if (isSpanFoundationCondition[endCondition]) {
    end = -width / 2;
  }

  const { offset, alignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });

  // Resolve top and bottom using foundation assembly datum abstractions if derived from horizontal assembly
  const defaultTop = floorPathTop(level, floorPath) - slabDepthAtFoundationPath({ condition }, slabSettings);
  const defaultBottom = worldInchesNew(foundation);
  const { top, depth } = resolveFoundationComponentTopBottom({
    foundationPath,
    level,
    defaultTop,
    defaultBottom,
  });

  const { openings } = isolatedFoundationPathOpenings({
    foundationPath,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent: footing,
    sectionType,
    sectionId,
    top,
    depth,
    width,
    offset,
    start,
    end,
    alignment,
    openings,
    material,
  });
}

function makeTurndownBrickLedgeSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  footing,
  doorSettings,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: TurndownFootingEntity;
  doorSettings: DoorSettings;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const { brickLedgeType, brickLedgeWidth, brickLedgeBehavior, material } = footing;
  const brickLedgeTopMax = floorPath ? floorPathTop(level, floorPath) : undefined;

  const { alignment: parentAlignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });

  const brickLedgeOffset = parentAlignment === FoundationSectionAlignment.CENTER ? footing.footingWidth / 2 : 0;

  return makeBrickLedgeSize({
    level,
    foundationPath,
    floorPath,
    sectionComponent: footing,
    pathEnds,
    brickLedgeType,
    brickLedgeBehavior,
    brickLedgeWidth,
    brickLedgeTopMax,
    brickLedgeOffset,
    doorSettings,
    isolatedFoundationSectionSizes,
    material,
    planarGraphs,
  });
}

export function makeStripFootingSectionSizes({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  footing,
  stemWall,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: StripFootingEntity;
  stemWall: StemWallEntity;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const footingSize = makeStripFootingSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    footing,
    stemWall,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  return [footingSize];
}

export function makeIsolatedFootingSize({
  level,
  foundationPath,
  floorPath,
  footing,
  pier,
  isolatedAssembly,
  planarGraphs,
}: {
  level: Level;
  foundationPath: Pick<FoundationPath, "condition" | "subSystemOffset" | "datums" | "planarGraphId" | "halfEdgeIndex"> &
    Partial<Pick<FoundationPath, "assembly">>;
  floorPath?: Pick<FloorPath, "floorPriority">;
  footing: RectangularIsolatedFootingEntity | CircularIsolatedFootingEntity;
  pier: PierEntity | undefined;
  isolatedAssembly: SolidFoundationAssembly;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { condition } = foundationPath;
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
  const sectionType = FoundationSectionType.COMPONENT;
  const { assemblyId } = isolatedAssembly;
  const sectionId = foundationSectionId(footing.componentType, sectionType, undefined, assemblyId);

  if (vec2DistSquared(startX, startY, endX, endY) < DIST_EPSILON_SQUARED) {
    return voidFoundationSectionSizeNew(footing, sectionId);
  }

  let footingWidth = 0,
    pierHalfWidth = 0;
  const { footingDepth, material } = footing;

  if (isCircularIsolatedFooting(footing)) {
    footingWidth = footing.footingRadius * 2;
  } else {
    footingWidth = footing.footingWidth;
  }
  if (pier) {
    if (isCircularCMUPier(pier) || isCircularCIPPier(pier)) {
      pierHalfWidth = pier.pierRadius;
    } else if (isRectangularCMUPier(pier) || isRectangularCIPPier(pier)) {
      pierHalfWidth = pier.pierWidth / 2;
    }
  } else {
    pierHalfWidth = 0.5 * footingWidth;
  }

  const top = isolatedFootingTop({
    foundationPath,
    level,
    footing,
    isolatedAssembly,
  });

  const { offset: pierOffset, alignment: pierAlignnment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  const alignment = FoundationSectionAlignment.CENTER;
  let offset = pierOffset;
  if (!isSpanFoundationCondition[condition]) {
    if (pierAlignnment === FoundationSectionAlignment.LEFT) {
      offset -= pierHalfWidth;
    } else if (pierAlignnment === FoundationSectionAlignment.RIGHT) {
      offset += pierHalfWidth;
    }
  }

  return isolatedFoundationSectionSizeFromAlignment({
    sectionComponent: footing,
    sectionType,
    sectionId,
    top,
    depth: footingDepth,
    width: footingWidth,
    alignment,
    assemblyId,
    offset,
    material,
    pickSource: isolatedAssembly.pickSource,
  });
}

function makeStripFootingSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  footing,
  stemWall,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: StripFootingEntity;
  stemWall: StemWallEntity;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { condition } = foundationPath;
  // Freeform / horizontal-assembly foundation paths have no floorPath. Default interior to
  // the LEFT of the path (exterior RIGHT), matching brick ledge's freeform convention, so
  // lateralOffset and brick-ledge footing padding still shift toward the exterior.
  const { floorPriority } = floorPath ?? {
    floorPriority: foundationPath.isDerivedFromHorizontalAssembly ? PathPriority.LEFT : PathPriority.NONE,
  };
  const leftPriority = floorPriority === PathPriority.LEFT;
  const hasFloorPriority = floorPriority !== PathPriority.NONE;

  // stem wall footings wrap the whole building, so we don't need per room class
  const sectionType = FoundationSectionType.COMPONENT;
  const sectionId = foundationSectionId(footing.componentType, sectionType);

  const {
    footingWidth,
    footingDepth,
    footingPadding: brickLedgeFootingPadding,
    lateralOffset: footingLateralOffset,
    material,
  } = footing;
  const { width: stemWallFullWidth } = stemWallWidth({ path: foundationPath, stemWall });

  const stemWallHalfWidth = stemWallFullWidth / 2;
  const top = stripFootingTop({
    foundationPath,
    level,
    footing,
  });
  const depth = footingDepth;

  // calculate width
  let width = footingWidth;

  // calculate ends
  const endConditions = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });
  const { startCondition, endCondition } = endConditions;
  let { start, end } = endConditions;
  let allowBrickLedgePaddingAtStart = false;
  let allowBrickLedgePaddingAtEnd = false;

  if (level.levelBelow === undefined || !isInteriorFoundationCondition[startCondition]) {
    start -= footingWidth / 2 - stemWallHalfWidth;
    allowBrickLedgePaddingAtStart = true;
  }

  if (level.levelBelow === undefined || !isInteriorFoundationCondition[endCondition]) {
    end -= footingWidth / 2 - stemWallHalfWidth;
    allowBrickLedgePaddingAtEnd = true;
  }

  const { offset: stemWallOffset, alignment: stemWallAlignnment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  const alignment = FoundationSectionAlignment.CENTER;
  let offset = stemWallOffset;
  if (!isSpanFoundationCondition[condition]) {
    if (stemWallAlignnment === FoundationSectionAlignment.LEFT) {
      offset -= stemWallHalfWidth;
    } else if (stemWallAlignnment === FoundationSectionAlignment.RIGHT) {
      offset += stemWallHalfWidth;
    }
  }

  const brickLedgeType = brickLedgeTypeAtCondition(condition, stemWall.brickLedgeType, stemWall.brickLedgeBehavior);
  if (brickLedgeType !== BrickLedgeType.NONE && isPerimeterFoundationCondition[condition]) {
    start -= allowBrickLedgePaddingAtStart ? brickLedgeFootingPadding : 0;
    end -= allowBrickLedgePaddingAtEnd ? brickLedgeFootingPadding : 0;
    width += brickLedgeFootingPadding;
    if (hasFloorPriority) {
      offset += leftPriority ? brickLedgeFootingPadding / 2 : -brickLedgeFootingPadding / 2;
    }

    if (brickLedgeType === BrickLedgeType.INSET && stemWall.stemWallType === StemWallType.CIP) {
      const brickLedgeWidth = stemWall.brickLedgeWidth;
      if (hasFloorPriority) {
        offset += leftPriority ? brickLedgeWidth / 2 : -brickLedgeWidth / 2;
      }
    }
  }

  const { openings } = isolatedFoundationPathOpenings({
    foundationPath,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  // Apply footing lateral offset if specified
  // Positive lateralOffset shifts footing toward exterior (same convention as brick ledge)
  if (footingLateralOffset && hasFloorPriority) {
    offset += leftPriority ? footingLateralOffset : -footingLateralOffset;
  }

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent: footing,
    sectionType,
    sectionId,
    top,
    depth,
    width,
    offset,
    start,
    end,
    alignment,
    openings,
    material,
  });
}

export function isolatedFootingTop({
  foundationPath,
  level,
  footing,
  isolatedAssembly,
}: {
  foundationPath: Pick<FoundationPath, "condition" | "datums"> & Partial<Pick<FoundationPath, "assembly">>;
  level: Level;
  footing: RectangularIsolatedFootingEntity | CircularIsolatedFootingEntity;
  isolatedAssembly: SolidFoundationAssembly;
}) {
  const { footingDepth } = footing;
  const datumAbstraction =
    foundationPath.assembly?.bottomDatumAbstraction ??
    isolatedAssembly.bottomDatumAbstraction ??
    defaultFoundationDatumAbstractions.bottomDatum;

  let top = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  top = worldInchesNew(top + footingDepth);

  return top;
}

function stripFootingTop({
  foundationPath,
  level,
  footing,
}: {
  foundationPath: FoundationPath;
  level: Level;
  footing: StripFootingEntity;
}) {
  const { footingDepth } = footing;
  const datumAbstraction =
    foundationPath.assembly.bottomDatumAbstraction ?? defaultFoundationDatumAbstractions.bottomDatum;

  let top = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  top = worldInchesNew(top + footingDepth);

  return top;
}
